import { useEffect, useRef } from 'react'

import { useSalesDemoChatbot } from '../hooks/useSalesDemoChatbot'
import { useTypingPhrase } from '../hooks/useTypingPhrase'

import { Box, Flex, Text, VStack } from '@chakra-ui/react'

import type { IncidentContextI } from '../types'
import { ChatInput } from './ChatInput'
import { ChatMessage } from './ChatMessage'
import { SuggestedQuestions } from './SuggestedQuestions'
import { TypingIndicator } from './TypingIndicator'

const EMPTY_STATE_MESSAGE =
  'Ask questions about standard operating procedures for this incident. Answers are grounded in facility-specific SOPs.'

interface SOPChatbotIProps {
  incidentContext: IncidentContextI | null
}

export const SOPChatbot = ({ incidentContext }: SOPChatbotIProps) => {
  const {
    messages,
    isLoading,
    suggestedQuestions,
    error,
    sendMessage,
    isAutoPlaying,
  } = useSalesDemoChatbot(incidentContext)
  const typingPhrase = useTypingPhrase()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [messages, isLoading])

  const hasMessages = messages.length > 0

  return (
    <Flex direction='column' h='calc(100vh - 56px)'>
      {/* Messages area */}
      <Box
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: '#B3BFC6', // darkblue-100
            borderRadius: '24px',
          },
        }}
        flex={1}
        overflow='auto'
        px='24px'
        py='16px'
        ref={scrollRef}
      >
        {!hasMessages && !isLoading && (
          <VStack py={8} spacing={4}>
            <Text
              color='text.secondary.light'
              fontSize='14px'
              textAlign='center'
            >
              {EMPTY_STATE_MESSAGE}
            </Text>
          </VStack>
        )}

        <VStack align='stretch' spacing={3}>
          {messages.map((msg) => (
            <ChatMessage
              actor={msg.actor}
              key={msg.id}
              sources={msg.sources}
              text={msg.text}
            />
          ))}
          {isLoading && <TypingIndicator phrase={typingPhrase} />}
        </VStack>

        {error && (
          <Box
            bg='red.50'
            borderRadius='md'
            color='red.600'
            fontSize='13px'
            mt={3}
            p={3}
          >
            {error}
          </Box>
        )}

        {/* Suggested questions below messages */}
        {suggestedQuestions.length > 0 && !isLoading && !isAutoPlaying && (
          <Box mt={hasMessages ? 4 : 0}>
            <SuggestedQuestions
              isDisabled={isLoading}
              onSelect={sendMessage}
              questions={suggestedQuestions}
            />
          </Box>
        )}
      </Box>

      {/* Input area */}
      <Box
        borderTop='1px solid'
        borderTopColor='border.100'
        px='24px'
        py='12px'
      >
        <ChatInput
          isDisabled={isLoading || isAutoPlaying}
          onSend={sendMessage}
        />
      </Box>
    </Flex>
  )
}
